import { ExtraSmallButton } from '../../custom'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectChartReplayMode, selectTimeBuckets } from '../../../selectors/chart'
import { disableReplayMode, ReplayStatus, setReplayModeState, updateReplayModeState } from '../../../reducer/chart.reducer'
import { AppContext } from '../../../App'
import ReplayIcon from '../../../assets/icons/ReplayIcon'
import { Tooltip } from '@mui/material'
import { ShortcutTooltipTitle, ShortcutKeys } from '../../KeyboardShortcuts/ShortcutTooltipTitle'
import { selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import ReusableDialog from '../../common/reusableDialog'
import { IChart } from '../../../../types/chart.interface'
import { selectLayoutWidgets } from '../../../selectors/widgetDashboard'
import { TimeBucket } from '../../../proto/generated/common'
import { getTimeBucketLengthInSeconds, isIntraday } from '../../../util/timeBuckets'
import { WidgetTypes } from '../../widgetDashboard/widgetDashboard.enums'


type ChartReplayButtonProps = {
    viewKey: string
}

    const ChartReplayButton: React.FC<ChartReplayButtonProps> = ({viewKey}) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const activeLayoutId = useSelector(selectActiveLayoutId);
    const activeLayout = useSelector(selectActiveLayout)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const timeBuckets = useSelector(selectTimeBuckets)
    const replayState = useSelector(selectChartReplayMode);
    const [showCancelConfirmPopup, setShowCancelConfirmPopup] = useState(false);
    const replayManager = appContext.replayManager

    const onReplayTimeSelection = useCallback((maxAvailableTimeBucket: TimeBucket) => {
        const isMaxBucketIntraday = isIntraday(maxAvailableTimeBucket.timeSeries);
        const maxBucketDuration = getTimeBucketLengthInSeconds(maxAvailableTimeBucket)
        let availableTimeBuckets: TimeBucket[] = []
        timeBuckets.forEach((tb) => {
            const bucketDuration = getTimeBucketLengthInSeconds({timeSeries: tb.timeSeries, value: tb.timeValue});

            const isIntradayBucket = isIntraday(tb.timeSeries);

            if ((isMaxBucketIntraday && isIntradayBucket && maxBucketDuration%bucketDuration === 0) || (!isMaxBucketIntraday && isIntradayBucket)) {
                availableTimeBuckets.push({timeSeries: tb.timeSeries, value: tb.timeValue})
                return;
            }

            if (!isMaxBucketIntraday && !isIntradayBucket && maxBucketDuration%bucketDuration === 0) {
                availableTimeBuckets.push({timeSeries: tb.timeSeries, value: tb.timeValue})
                return;
            }
        })
        dispatch(updateReplayModeState({
            status: ReplayStatus.SELECTED,
            maxAvailableTimeBucket,
            selectedTimeBucket: maxAvailableTimeBucket,
            availableTimeBuckets
        }))
    }, [timeBuckets])

    const onReplayComplete = useCallback(() => {
        dispatch(updateReplayModeState({ status: ReplayStatus.SELECTED, isReplayComplete: true }))
    }, [dispatch])

    useEffect(() => {
        replayManager.updateCallbacks({
            onReplayTimeSelection,
            onReplayComplete
        })
    }, [onReplayTimeSelection, onReplayComplete])


    const onReplayClick = useCallback(async () => {

        if (!replayState) {
            dispatch(setReplayModeState({
                viewKey,
                layoutId: activeLayoutId,
                status: ReplayStatus.SELECTION,
                availableTimeBuckets: []
            }));

            let charts: Array<IChart> = [];

            // Include CHART and OPTION_CHART - both use chartV2 Chart (IChart) and support replay.
            // Scalper and multipane layouts have OPTION_CHART widgets (call/put option charts).
            const replayableWidgetTypes = [WidgetTypes.CHART, WidgetTypes.OPTION_CHART];
            if (activeLayoutId && widgets) {
                Object.values(widgets).forEach((w) => {
                    if (replayableWidgetTypes.includes(w.widgetType)) {
                        const chartId = w.settings?.chartId ?? `${activeLayoutId}-${w.slotId}`;
                        const chart = appContext.getChart(chartId);
                        if (chart && !charts.includes(chart)) {
                            charts.push(chart);
                        }
                    }
                });
            }

            if (charts.length === 0) {
                charts = [appContext.getChart(viewKey)];
            }

            await replayManager.initialize(charts);
            await replayManager.enableReplayMode()
            return;
        }

        if (replayState.status === ReplayStatus.SELECTION) {
            await onCancelReplayConfirm();
            return;
        }

        if (replayState.isReplayComplete) {
            await onCancelReplayConfirm();
            return;
        }

        setShowCancelConfirmPopup(true)
    }, [activeLayoutId, widgets, activeLayout, viewKey, replayState])

    useEffect(() => {
    }, [viewKey, activeLayoutId])

    const onCancelReplayConfirm = useCallback(async () => {
        await replayManager.disableReplayMode()
        await replayManager.reset();
        dispatch(disableReplayMode());
    }, [])

    // const shortcuts: ShortcutType = useMemo(() => ({
    //     [ChartReplayShortcutsKeyName.openCloseChartReplayDialog]: handleKeyClick
    // }), [handleKeyClick])

    // useShortcuts(ShortcutsCategories.CHART_REPLAY, shortcuts)


    return (
        <>
            <Tooltip
                placement="bottom"
                arrow
                title={
                    <ShortcutTooltipTitle label="Chart Replay">
                        <ShortcutKeys keys={['⇧', 'R']} />
                    </ShortcutTooltipTitle>
                }
            >
                <ExtraSmallButton id={'chart-replay-button'}
                onClick={onReplayClick}
                    variant={replayState ? 'contained' : 'text'}
                    size="small" color={replayState ? 'primary' : 'inherit'} data-event={'Replay Button clicked'}
                    data-event-source={'stockbar'}>
                    <ReplayIcon />
                </ExtraSmallButton>
            </Tooltip>
            <ReusableDialog
                title="Leave current replay?"
                content=""
                onClose={() => setShowCancelConfirmPopup(false)}
                onSubmit={onCancelReplayConfirm}
                open={showCancelConfirmPopup}
                cancelText="Stay"
                submitText="Leave"
                cancelVariant="outlined"
                submitVariant="contained"
                submitButtonProps={{ color: 'primary' }}
            />
        </>
    )
}

export default ChartReplayButton